import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import {
  Component, ContentChildren, ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output, QueryList,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  ISearchResultItemGeneric,
  SearchFunction
} from '@shared/components/search/multiple-select-with-search/multiple-select-with-search.component';
import { ICdkOverlayTriggerParams } from '@shared/components/tiny-kit/cdk-overlay/cdk-overlay.component';
import { TranslateUsersPipe } from '@shared/pipes/translate-users/translate-users.pipe';
import { StringUtils } from '@shared/utils/string.utils';
import { BehaviorSubject, of, Subject } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'app-multiple-select-base',
  templateUrl: './multiple-select-base.component.html',
  styleUrls: ['./multiple-select-base.component.scss'],
  providers: [ TranslateUsersPipe ],
  exportAs: 'multipleSelectBase'
})
export class MultipleSelectBaseComponent<T extends number | string> implements OnInit, OnChanges {
  @Input() placeholder: string;
  @Input() isMultipleSelect = true;
  @Input() checkboxAllChecked = true;
  @Input() showCustomTrigger: boolean;
  @Input() passedControl: FormControl;
  @Input() searchFn: SearchFunction<T>;
  @Input() showCustomSelectList: boolean;
  @Input() matTooltipLimitDisabled: number;
  @Input() triggerResetFilter$: Subject<void>;
  @Input() isDisabled: 'off' | boolean = 'off';
  @Input() staticList: ISearchResultItemGeneric<T>[];
  @Input() selectedItems: ISearchResultItemGeneric<T>[] = [];
  @Input() searchFieldMode: 'full' | 'only-check-all' | 'hide' = 'full';
  @Output() selectionChange = new EventEmitter<ISearchResultItemGeneric<T>[]>();
  @Output() singleSelectionChange = new EventEmitter<ISearchResultItemGeneric<T>>();
  @Output() checkboxAllCheckedChange = new EventEmitter<boolean>();

  resultList$ = new BehaviorSubject<ISearchResultItemGeneric<T>[]>([]);
  openCdkOverlay$ = new BehaviorSubject<ICdkOverlayTriggerParams>({ isOpen: false });
  selectListOpen$ = this.openCdkOverlay$.pipe(map(p => p.isOpen));
  searchControl = new FormControl<string>('');
  selectedItemMap: Map<T, ISearchResultItemGeneric<T>> = new Map<T, ISearchResultItemGeneric<T>>([]);

  isLoading = true;
  triggerTextIsPlaceholder: boolean = true;
  triggerTextOutput: string;
  triggerText: Set<string> = new Set<string>();

  SelectItemType: ISearchResultItemGeneric<T>;

  constructor(
    private translateUsersPipe: TranslateUsersPipe
  ) { }

  ngOnChanges(changes: SimpleChanges) {
    if (this.staticList?.length && !this.resultList$.getValue().length) {
      this.isLoading = false;
      this.resultList$.next(this.staticList);
    }

    if (this.selectedItems?.length) {
      const checked = { checked: true } as MatCheckboxChange;
      this.selectedItems.forEach(p => this.multipleCheckItem(checked, p));
    }
  }

  ngOnInit(): void {
    this.updateTriggerText();
    this.resetTriggerSubscribe();

    this.searchControl.valueChanges.pipe(
      untilDestroyed(this),
      switchMap((v) => {
        this.resultList$.next([]);
        this.isLoading = true;
        return this.searchFn(v);
      }),
      catchError((e) => {
        console.error(e);
        this.isLoading = false;
        return of([]);
      }),
    ).subscribe({
      next: res => {
        this.isLoading = false;
        this.updateSelectionAndSetResultList(res);
      }
    });
  }

  multipleCheckItem({ checked }: MatCheckboxChange, item: ISearchResultItemGeneric<T>) {
    item.selected = checked;
    if (checked) {
      this.selectItem(item);
    } else {
      this.unselectItem(item)
    }
    if (this.triggerText.size) {
      this.checkboxAllChecked = false;
    }
    if (this.passedControl) {
      const selectedItemsAsArray = Array.from(this.selectedItemMap.keys());
      this.passedControl.setValue(selectedItemsAsArray)
    }
    this.updateTriggerText();
    const selectedItemsAsArray = Array.from(this.selectedItemMap.values());
    this.selectionChange.emit(selectedItemsAsArray);
  }

  singleSelectItem(item: ISearchResultItemGeneric<T>) {
    this.triggerText = new Set([item.name]);
    this.selectedItemMap = new Map([[item.id, item]]);
    this.singleSelectionChange.emit(item);
    this.updateTriggerText();
  }

  private selectItem(item: ISearchResultItemGeneric<T>) {
    this.triggerText.add(item.name);
    const currentMap = this.selectedItemMap.entries();
    this.selectedItemMap = new Map([ ...currentMap, [item.id, item]]);
  }

  private unselectItem(item: ISearchResultItemGeneric<T>) {
    this.triggerText.delete(item.name);
    this.selectedItemMap.delete(item.id);
  }

  triggerSelect(trigger: CdkOverlayOrigin, element: HTMLDivElement) {
    if (this.isDisabled === true) { return; }
    this.openCdkOverlay$.next({
      origin: trigger,
      elementWidth: element.clientWidth,
      isOpen: true
    });
    if (!this.resultList$.getValue().length) {
      this.searchControl.setValue(this.searchControl.value);
    }
  }

  checkAll(emitEvent = true) {
    if (!this.selectedItemMap.size) {
      setTimeout(() => {
        this.checkboxAllChecked = true;
        this.checkboxAllCheckedChange.emit();
      });
      return;
    }
    console.log('checkAll');
    const itemsToUnselected = this.resultList$.getValue().map(p => {
      p.selected = false;
      return p;
    });
    this.resultList$.next(itemsToUnselected);
    this.triggerText.clear();
    this.updateTriggerText();
    this.selectedItemMap.clear();

    setTimeout(() => {
      this.checkboxAllChecked = true;
      if (emitEvent) {
        this.selectionChange.emit([]);
        this.checkboxAllCheckedChange.emit();
      }
    });
  }

  updateTriggerText() {
    this.triggerTextIsPlaceholder = false;
    const triggerText = this.triggerText.values();
    const triggerTextSize = this.triggerText.size;
    console.log({ triggerText, triggerTextSize });
    if (!triggerTextSize) {
      this.triggerTextIsPlaceholder = true;
      this.triggerTextOutput = this.placeholder;
      return;
    }
    if (triggerTextSize > 3) {
      const words = [$localize`элемент`, $localize`элемента`, $localize`элементов`];
      const declinationResult = StringUtils.declination(words, triggerTextSize);
      this.triggerTextOutput = $localize`Выбрано` + ' ' + triggerTextSize + ' ' + declinationResult;
      return;
    }

    this.triggerTextOutput = Array.from(triggerText).map(p => {
      return this.translateUsersPipe.transform(p)
    }).join(', ');
  }

  private updateSelectionAndSetResultList(newResultList: ISearchResultItemGeneric<T>[]) {
    const selectedValues = Array.from(this.selectedItemMap.keys());
    selectedValues.forEach(id => {
      const item = newResultList.find(p => p.id === id);
      if (item) { item.selected = true; }
    });

    this.resultList$.next(newResultList);
  }

  private resetTriggerSubscribe() {
    if (!this.triggerResetFilter$) { return; }
    this.triggerResetFilter$.pipe(untilDestroyed(this)).subscribe({
      next: () => {
        this.checkAll(false);
        this.passedControl.setValue([]);
        this.searchControl.setValue('', { emitEvent: false });
      }
    })
  }
}
